import type { FC } from 'react';
import { useState } from 'react';

import { useManagedCompanies } from '../../hooks/use-managed-companies';
import { createSimpleCompany } from '../../models/simple-company';
import type { SimpleCompanyItem } from '../../types';
import { useNwtTrackingCompanies } from '../../utils/tracking';
import { PageError } from '../page-error';
import { SectionContainer } from '../section-container/section-container';
import { ShowMoreButton } from '../show-more-button';
import { SimpleCompanyCardGrid } from '../simple-company-card-grid/simple-company-card-grid';

type SectionManagedCompaniesProps = {
  trackingPage: string;
};

export const SectionManagedCompanies: FC<SectionManagedCompaniesProps> = ({
  trackingPage,
}) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const trackNwtEvent = useNwtTrackingCompanies();

  const { data, error, loading, fetchMoreManagedCompanies } =
    useManagedCompanies();
  const currentCompanies = data?.viewer?.managedCompanies?.edges ?? [];

  if (loading && currentCompanies.length === 0) {
    return null;
  }

  if (error) {
    return <PageError />;
  }

  if (currentCompanies.length === 0) {
    return null;
  }

  const companiesCards = currentCompanies.reduce<SimpleCompanyItem[]>(
    (cards, item) => {
      const company = item?.node?.company;
      const simpleCompany = company
        ? createSimpleCompany({
            company,
          })
        : undefined;

      if (!company || !simpleCompany) {
        return cards;
      }

      cards.push(simpleCompany);

      return cards;
    },
    []
  );

  if (companiesCards.length === 0) {
    return null;
  }

  const { hasNextPage, endCursor } =
    data?.viewer?.managedCompanies?.pageInfo ?? {};

  const handleShowMore = async () => {
    if (!endCursor) {
      return;
    }

    setIsFetchingMore(true);
    try {
      await fetchMoreManagedCompanies(endCursor);
    } finally {
      setIsFetchingMore(false);
    }

    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/overview',
      event: 'clicked',
      element: 'show_more',
    });
  };

  return (
    <SectionContainer title="CP_MANAGED_COMPANIES_HEADER">
      <>
        <SimpleCompanyCardGrid
          items={companiesCards}
          trackingPage={trackingPage}
        />
        {hasNextPage && (
          <ShowMoreButton
            disabled={isFetchingMore}
            loading={isFetchingMore}
            onClick={() => {
              void handleShowMore();
            }}
            intlKey="CP_OTHER_COMPANIES_MANAGE"
          />
        )}
      </>
    </SectionContainer>
  );
};
